<div
  class="mixture-card border border-gray-200 bg-white rounded-xl shadow-sm p-4 flex flex-col gap-4 dark:border-gray-700 dark:bg-slate-900/80"
>
  @if (mixtureBannerErrors.length) {
    <p-message severity="warn" [text]="mixtureBannerErrorsText" [closable]="false" />
  }

  <div class="mixture-card-header flex flex-wrap items-start justify-between gap-3">
    <div class="mixture-card-header-text flex flex-col gap-1 min-w-0 flex-1">
      <div class="flex flex-wrap items-center gap-2 min-w-0">
        <p-tag severity="info" [value]="mixtureBadgeLabelKey | translate" />
        <span class="text-gray-900 font-semibold text-base truncate dark:text-gray-100">{{ mixture.name }}</span>
      </div>
      @if (mixture.description) {
        <p class="mixture-card-description">{{ mixture.description }}</p>
      }
    </div>
    <div class="flex flex-wrap items-center gap-2 shrink-0">
      <p-button
        type="button"
        icon="pi pi-pencil"
        [label]="'reports.fill.edit' | translate"
        [text]="true"
        severity="secondary"
        (onClick)="onEditClick()"
      />
      <p-button
        type="button"
        icon="pi pi-trash"
        [label]="'reports.fill.delete' | translate"
        severity="danger"
        (onClick)="openDeleteDialog()"
      />
    </div>
  </div>

  <eko-generic-table
    #recordsTable
    styleClass="p-datatable-sm p-datatable-gridlines p-datatable-striped mixture-records-table"
    [columnRows]="recordColumnRows"
    [dataProvider]="recordDataProvider"
    [tableConfig]="recordTableConfig"
  >
    <ng-template #rowActionsTemplate let-rowData>
      <div class="mixture-actions-col">
          <p-button
            type="button"
            icon="pi pi-pencil"
            [rounded]="true"
            [text]="true"
            severity="success"
            [attr.aria-label]="'reports.fill.editRow' | translate"
            (onClick)="openRecordDialogEdit(rowData)"
          />
          @if (mixture.type !== 'WELDING') {
            <p-button
              type="button"
              icon="pi pi-trash"
              [rounded]="true"
              [text]="true"
              severity="danger"
              [attr.aria-label]="'reports.fill.deleteRow' | translate"
              (onClick)="openRecordDeleteDialog(rowData)"
            />
          }
      </div>
    </ng-template>
  </eko-generic-table>

  @if (recordRows.length === 0) {
    <div class="text-gray-600 text-sm py-1 dark:text-gray-400">
      {{ 'reports.fill.emptyRecords' | translate }}
    </div>
  }

  @if (mixture.type !== 'WELDING') {
    <p-button
      type="button"
      styleClass="mixture-add-button w-full"
      icon="pi pi-plus"
      [label]="'reports.fill.addSubstance' | translate"
      [outlined]="true"
      severity="secondary"
      (onClick)="openRecordDialogCreate()"
    />
  }

  <div
    class="mixture-footer flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-gray-800 border-t border-gray-200 pt-3 dark:border-gray-700 dark:text-gray-200"
  >
    <span class="font-semibold">{{ 'reports.fill.mixtureFooterMass' | translate }}</span>
    <span>{{ mixture.mass | number: '1.0-4' }} {{ 'reports.fill.unitKg' | translate }}</span>
    <span class="text-gray-400 dark:text-gray-500">|</span>
    <span class="font-semibold">{{ 'reports.fill.mixtureFooterEmission' | translate }}</span>
    <span>{{ mixture.totalEmission | number: '1.0-8' }} {{ 'reports.fill.unitKg' | translate }}</span>
    <span class="text-gray-400 dark:text-gray-500">|</span>
    <span class="font-semibold">{{ 'reports.fill.mixtureFooterFactorSum' | translate }}</span>
    <span
      class="mixture-footer-factor-sum font-semibold"
      [class.mixture-footer-factor-sum--warning]="isTotalEmissionFactorOverLimit"
      [pTooltip]="'reports.fill.emissionFactorOverLimitTooltip' | translate"
      [tooltipDisabled]="!isTotalEmissionFactorOverLimit"
      tooltipPosition="top"
      >{{ mixture.totalEmissionFactor | number: '1.0-8' }} {{ 'reports.fill.unitKgPerMg' | translate }}</span>
  </div>

  <p-dialog
    [header]="recordDialogHeaderKey | translate"
    [(visible)]="recordDialogVisible"
    [modal]="true"
    styleClass="report-mixture-record-dialog eko-responsive-dialog"
    [style]="{ width: 'min(34rem, 94vw)' }"
    [draggable]="false"
    [resizable]="false"
    [dismissableMask]="!recordDialogSaving"
    [closable]="!recordDialogSaving"
    (onHide)="closeRecordDialog()"
  >
    <form class="record-dialog-form" [formGroup]="recordForm" (ngSubmit)="saveRecord()">
      <div class="field">
        <label class="field-label" for="record-cas">
          {{ 'reports.fill.recordDialog.casLabel' | translate }}<span class="text-required">*</span>
        </label>

        @if (isRecordEditMode) {
          <input
            id="record-cas"
            pInputText
            class="w-full record-cas-disabled"
            [value]="recordEditDisplayLabel"
            [readonly]="true"
            [disabled]="true"
            tabindex="-1"
          />
        } @else {
          <p-autocomplete
            inputId="record-cas"
            formControlName="substance"
            [suggestions]="recordAutocompleteItems"
            optionLabel="inputLabel"
            [emptyMessage]="'reports.fill.recordDialog.noCasNumber' | translate"
            [dropdown]="false"
            [forceSelection]="true"
            [minLength]="1"
            [delay]="200"
            [showClear]="true"
            styleClass="w-full"
            [inputStyle]="{ width: '100%' }"
            inputStyleClass="cas-autocomplete-input"
            panelStyleClass="cas-autocomplete-panel"
            appendTo="body"
            (completeMethod)="searchSubstances($event)"
          >
            <ng-template #item let-item>
              <div class="cas-autocomplete-option">
                <div class="cas-autocomplete-option__cas">{{ item.casDisplay }}</div>
                @if (item.nameDisplay) {
                  <div
                    class="cas-autocomplete-option__name"
                    [title]="item.nameFull || item.nameDisplay"
                  >
                    {{ item.nameDisplay }}
                  </div>
                }
              </div>
            </ng-template>
          </p-autocomplete>
        }

        @if (recordFieldError('substance'); as err) {
          <small class="p-error">{{ err }}</small>
        }
      </div>

      <div class="field">
        <label class="field-label" for="record-percent">
          {{ 'reports.fill.recordDialog.percentageLabel' | translate }}<span class="text-required">*</span>
        </label>
        <input
          id="record-percent"
          pInputText
          formControlName="usagePercentage"
          class="w-full"
          inputmode="decimal"
          autocomplete="off"
        />
        @if (recordFieldError('usagePercentage'); as err) {
          <small class="p-error">{{ err }}</small>
        }
      </div>
    </form>
    <ng-template pTemplate="footer">
      <div class="record-dialog-footer">
        <p-button
          type="button"
          [label]="'reports.dialog.cancel' | translate"
          severity="secondary"
          [disabled]="recordDialogSaving"
          (onClick)="closeRecordDialog()"
        />
        <p-button
          type="button"
          [label]="'reports.dialog.save' | translate"
          [loading]="recordDialogSaving"
          [disabled]="recordDialogSaving || recordForm.invalid"
          (onClick)="saveRecord()"
        />
      </div>
    </ng-template>
  </p-dialog>

  <p-dialog
    [header]="'reports.fill.recordDeleteTitle' | translate"
    [(visible)]="recordDeleteDialogVisible"
    [modal]="true"
    styleClass="report-mixture-delete-dialog eko-responsive-dialog"
    [style]="{ width: 'min(28rem, 92vw)' }"
    [draggable]="false"
    [resizable]="false"
    [dismissableMask]="!recordDeleteSaving"
    [closable]="!recordDeleteSaving"
  >
    <p class="mixture-delete-message m-0">
      {{ 'reports.fill.recordDeleteMessage' | translate: { cas: displayCasNumber(recordToDelete?.casNumber) || '—' } }}
    </p>
    <ng-template pTemplate="footer">
      <div class="mixture-delete-dialog-footer">
        <p-button
          type="button"
          [label]="'reports.dialog.no' | translate"
          severity="secondary"
          [disabled]="recordDeleteSaving"
          (onClick)="cancelRecordDeleteDialog()"
        />
        <p-button
          type="button"
          [label]="'reports.dialog.yes' | translate"
          severity="danger"
          [loading]="recordDeleteSaving"
          [disabled]="recordDeleteSaving"
          (onClick)="confirmRecordDelete()"
        />
      </div>
    </ng-template>
  </p-dialog>

  <p-dialog
    [header]="'reports.fill.mixtureDeleteTitle' | translate"
    [(visible)]="deleteDialogVisible"
    [modal]="true"
    styleClass="report-mixture-delete-dialog eko-responsive-dialog"
    [style]="{ width: 'min(28rem, 92vw)' }"
    [draggable]="false"
    [resizable]="false"
    [dismissableMask]="!deleteSaving"
    [closable]="!deleteSaving"
  >
    <p class="mixture-delete-message m-0">
      {{ 'reports.fill.mixtureDeleteMessage' | translate: { name: mixture.name } }}
    </p>
    <ng-template pTemplate="footer">
      <div class="mixture-delete-dialog-footer">
        <p-button
          type="button"
          [label]="'reports.dialog.no' | translate"
          severity="secondary"
          [disabled]="deleteSaving"
          (onClick)="cancelDeleteMixture()"
        />
        <p-button
          type="button"
          [label]="'reports.dialog.yes' | translate"
          severity="danger"
          [loading]="deleteSaving"
          [disabled]="deleteSaving"
          (onClick)="confirmDeleteMixture()"
        />
      </div>
    </ng-template>
  </p-dialog>
</div>
